import React from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import MapPdfDocument from './MapPdfDocument';

function waitForNextPaint() {
    return new Promise(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
}

async function waitForFonts() {
    if (!document.fonts?.ready) {
        return;
    }

    await document.fonts.ready;
}

async function waitForImages(container) {
    const images = Array.from(container.querySelectorAll('img'));

    await Promise.all(images.map(image => {
        if (image.complete && image.naturalWidth > 0) {
            return Promise.resolve();
        }

        return new Promise(resolve => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
        });
    }));
}

function buildOffscreenContainer() {
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-20000px';
    container.style.top = '0';
    container.style.zIndex = '-1';
    container.style.backgroundColor = '#ffffff';
    container.style.pointerEvents = 'none';

    document.body.appendChild(container);
    return container;
}

function getPageSize(pageElement) {
    return [
        Number(pageElement.getAttribute('data-page-width')),
        Number(pageElement.getAttribute('data-page-height')),
    ];
}

function getOrientation([pageWidth, pageHeight]) {
    return pageWidth >= pageHeight ? 'landscape' : 'portrait';
}

export function getMapPdfFileNameFromName(fileName, fallbackName = 'map') {
    const normalizedName = (fileName || fallbackName).trim();

    if (normalizedName.length === 0) {
        return 'map.pdf';
    }

    if (/\.[^.]+$/.test(normalizedName)) {
        return normalizedName.replace(/\.[^.]+$/, '.pdf');
    }

    return `${normalizedName}.pdf`;
}

export function getMapPdfFileName(revision, map) {
    if (revision?.mapId !== undefined && revision?.mapId !== null) {
        return `${revision.mapId}.pdf`;
    }

    return getMapPdfFileNameFromName(revision?.mapFileName, map?.description || 'map');
}

export function getSelectedMapLatestRevisions(maps = []) {
    return maps
        .filter(map => map?.selected && map?.latestRevision)
        .map(map => ({ map, revision: map.latestRevision }));
}

export async function createMapPdfDocument({ site, map, revision, mapImageUrl }) {
    const container = buildOffscreenContainer();
    const root = createRoot(container);

    try {
        flushSync(() => {
            root.render(
                <MapPdfDocument
                    map={map}
                    mapImageUrl={mapImageUrl}
                    revision={revision}
                    site={site}
                />
            );
        });

        await waitForFonts();
        await waitForImages(container);
        await waitForNextPaint();

        const pageElements = Array.from(container.querySelectorAll('[data-map-pdf-page]'));
        if (pageElements.length === 0) {
            throw new Error('Map PDF pages were not rendered.');
        }

        const firstPageSize = getPageSize(pageElements[0]);
        const pdf = new jsPDF({
            format: firstPageSize,
            orientation: getOrientation(firstPageSize),
            unit: 'pt',
        });

        for (const [pageIndex, pageElement] of pageElements.entries()) {
            const pageSize = getPageSize(pageElement);
            const [pageWidth, pageHeight] = pageSize;

            if (pageIndex > 0) {
                pdf.addPage(pageSize, getOrientation(pageSize));
            }

            const canvas = await html2canvas(pageElement, {
                backgroundColor: '#ffffff',
                logging: false,
                scale: 2,
                useCORS: true,
            });

            const imageUrl = canvas.toDataURL('image/png');
            pdf.addImage(imageUrl, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
        }

        return {
            blob: pdf.output('blob'),
            fileName: getMapPdfFileName(revision, map),
        };
    }
    finally {
        root.unmount();
        container.remove();
    }
}
